import { useState, useEffect } from "react";
import { API, formatApiErrorDetail } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Plus, Edit, Trash2, CreditCard, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { getLocalized } from "../../utils/localized";

export default function AdminBilling() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [plans, setPlans] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("plans");
  const [showAssign, setShowAssign] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [plansRes, subsRes, venuesRes] = await Promise.all([
        axios.get(`${API}/admin/billing/plans`),
        axios.get(`${API}/admin/billing/subscriptions`),
        axios.get(`${API}/admin/venues?limit=500`).catch(() => ({ data: { venues: [] } })),
      ]);
      setPlans(plansRes.data);
      setSubscriptions(subsRes.data);
      const vData = venuesRes.data;
      const list = Array.isArray(vData) ? vData : (vData?.venues || vData?.items || []);
      setVenues(list);
    } catch (error) {
      console.error("Error fetching billing:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (sub) => {
    if (!window.confirm(t("admin_billing:confirm_revoke", { venueName: sub.venue_name || sub.venue_id }))) return;
    try {
      await axios.delete(`${API}/admin/billing/subscriptions/${sub.venue_id}`);
      setSubscriptions((prev) => prev.filter((s) => s.venue_id !== sub.venue_id));
      toast.success(t("admin_billing:toast_revoked"));
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || t("admin_billing:toast_revoke_failed"));
    }
  };

  return (
    <div data-testid="admin-billing-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-medium text-foreground flex items-center gap-2"><CreditCard className="h-6 w-6 text-primary" />{t("admin_billing:title")}</h1>
        <div className="flex items-center gap-2">
          {activeTab === "subscriptions" && (
            <Dialog open={showAssign} onOpenChange={setShowAssign}>
              <DialogTrigger asChild>
                <Button data-testid="billing-assign-btn">
                  <Plus className="h-4 w-4 mr-2" /> {t("admin_billing:assign_plan_to_venue")}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>{t("admin_billing:assign_dialog_title")}</DialogTitle>
                </DialogHeader>
                <AssignForm
                  venues={venues}
                  plans={plans}
                  onSuccess={(s) => {
                    setSubscriptions((prev) => {
                      const others = prev.filter((x) => x.venue_id !== s.venue_id);
                      return [s, ...others];
                    });
                    setShowAssign(false);
                  }}
                />
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        <Button
          variant={activeTab === "plans" ? "default" : "outline"}
          size="sm"
          onClick={() => setActiveTab("plans")}
          data-testid="billing-tab-plans"
        >
          {t("admin_billing:tab_plans")} ({plans.length})
        </Button>
        <Button
          variant={activeTab === "subscriptions" ? "default" : "outline"}
          size="sm"
          onClick={() => setActiveTab("subscriptions")}
          data-testid="billing-tab-subscriptions"
        >
          {t("admin_billing:tab_subscriptions")} ({subscriptions.length})
        </Button>
      </div>

      {/* Plan create/edit dialog removed — plans are now a read-only mirror
          of /admin/venue-plans (the canonical 4-tier matrix). */}

      {loading ? (
        <div className="animate-pulse bg-muted h-64 rounded-xl" />
      ) : activeTab === "plans" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans.map((plan) => (
            <div
              key={plan.plan_id}
              className={`bg-card rounded-xl border p-5 flex flex-col transition-opacity ${
                plan.is_active ? "border-border" : "border-dashed border-muted-foreground/30 opacity-60"
              }`}
              data-testid={`plan-card-${plan.plan_id}`}
            >
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-lg font-medium text-foreground">{getLocalized(plan, "name", lang) || plan.name}</h3>
                <span className="text-xs text-muted-foreground">{plan.is_active ? t("admin_billing:card_active") : t("admin_billing:card_hidden")}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-1 mb-4">{getLocalized(plan, "description", lang) || plan.description}</p>
              <div className="mb-4">
                <span className="text-3xl font-semibold text-foreground">€{plan.price_monthly}</span>
                <span className="text-sm text-muted-foreground">{t("admin_billing:per_month")}</span>
              </div>
              {/* Plans are now a read-only mirror of /venue-plans (the
                  canonical 4-tier subscription matrix). Edit them under
                  Admin → Venue Plans where the feature_toggles live. */}
              <div className="text-xs text-muted-foreground italic mb-2">
                {t("admin_billing:plan_readonly_hint", { defaultValue: "Plans are managed under Venue Plans → feature toggles." })}
              </div>
            </div>
          ))}
        </div>
      ) : subscriptions.length === 0 ? (
        <div className="bg-card rounded-xl border border-border p-8 text-center">
          <CreditCard className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
          <h3 className="font-medium text-foreground mb-2">{t("admin_billing:no_subs_title")}</h3>
          <p className="text-sm text-muted-foreground">
            {t("admin_billing:no_subs_help")}
          </p>
        </div>
      ) : (
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("admin_billing:col_venue")}</TableHead>
                <TableHead>{t("admin_billing:col_plan")}</TableHead>
                <TableHead>{t("admin_billing:col_status")}</TableHead>
                <TableHead>{t("admin_billing:col_start")}</TableHead>
                <TableHead>{t("admin_billing:col_end")}</TableHead>
                <TableHead className="text-right">{t("admin_billing:col_actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {subscriptions.map((sub) => {
                const statusColor =
                  {
                    active: "bg-green-100 text-green-700",
                    paused: "bg-amber-100 text-amber-700",
                    expired: "bg-red-100 text-red-700",
                  }[sub.status] || "bg-muted text-muted-foreground";
                const matchedPlan = plans.find((p) => p.plan_id === sub.plan_id);
                const localizedPlanName = matchedPlan
                  ? (getLocalized(matchedPlan, "name", lang) || matchedPlan.name)
                  : sub.plan_name;
                return (
                  <TableRow key={sub.subscription_id} data-testid={`sub-row-${sub.subscription_id}`}>
                    <TableCell className="font-medium">{sub.venue_name || sub.venue_id || "—"}</TableCell>
                    <TableCell>{localizedPlanName}</TableCell>
                    <TableCell>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusColor}`}>
                        {t(`subscription_status.${sub.status}`, { defaultValue: sub.status })}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{sub.start_date || "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{sub.end_date || "—"}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setShowAssign(true);
                          // Pre-fill the form by stashing the row to edit; AssignForm reads via prop on next open
                          window.__sub_prefill = {
                            venue_id: sub.venue_id,
                            plan_id: sub.plan_id,
                            start_date: sub.start_date,
                            end_date: sub.end_date,
                            status: sub.status,
                          };
                        }}
                        data-testid={`sub-edit-${sub.subscription_id}`}
                      >
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRevoke(sub)}
                        data-testid={`sub-revoke-${sub.subscription_id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function PlanForm({ plan, onSuccess }) {
  const { t } = useTranslation();
  const isEdit = !!plan;
  const [form, setForm] = useState({
    name: plan?.name || "",
    name_el: plan?.name_el || "",
    slug: plan?.slug || "",
    description: plan?.description || "",
    description_el: plan?.description_el || "",
    price_monthly: plan?.price_monthly || 0,
    price_yearly: plan?.price_yearly || 0,
    features: (plan?.features || []).join("\n"),
    features_el: (plan?.features_el || []).join("\n"),
    is_active: plan?.is_active ?? true,
    sort_order: plan?.sort_order || 0,
  });
  const [loading, setLoading] = useState(false);
  const [langTab, setLangTab] = useState("en");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = {
      ...form,
      features: form.features.split("\n").map((f) => f.trim()).filter(Boolean),
      features_el: form.features_el.split("\n").map((f) => f.trim()).filter(Boolean),
    };
    try {
      const res = isEdit
        ? await axios.put(`${API}/admin/billing/plans/${plan.plan_id}`, payload)
        : await axios.post(`${API}/admin/billing/plans`, payload);
      toast.success(isEdit ? t("admin_billing:toast_plan_updated") : t("admin_billing:toast_plan_created"));
      onSuccess(res.data);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || t("admin_billing:toast_failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Language tabs */}
      <div className="flex items-center gap-1 p-1 bg-muted rounded-lg w-fit">
        <button
          type="button"
          onClick={() => setLangTab("en")}
          className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
            langTab === "en" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"
          }`}
          data-testid="plan-lang-en"
        >
          🇬🇧 English
        </button>
        <button
          type="button"
          onClick={() => setLangTab("el")}
          className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
            langTab === "el" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"
          }`}
          data-testid="plan-lang-el"
        >
          🇬🇷 Ελληνικά
        </button>
      </div>

      {langTab === "en" ? (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t("admin_billing:field_name_en")}</Label>
              <Input
                value={form.name}
                onChange={(e) =>
                  setForm({
                    ...form,
                    name: e.target.value,
                    ...(isEdit ? {} : { slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-") }),
                  })
                }
                required
                className="mt-1"
                data-testid="plan-name-input"
              />
            </div>
            <div>
              <Label>{t("admin_billing:field_slug")}</Label>
              <Input
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                required
                className="mt-1"
                disabled={isEdit}
              />
            </div>
          </div>
          <div>
            <Label>{t("admin_billing:field_description_en")}</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="mt-1"
              rows={2}
              data-testid="plan-description-input"
            />
          </div>
          <div>
            <Label>{t("admin_billing:field_features_en")}</Label>
            <Textarea
              value={form.features}
              onChange={(e) => setForm({ ...form, features: e.target.value })}
              className="mt-1 min-h-[100px]"
              placeholder={t("admin_billing:field_features_en_placeholder")}
              data-testid="plan-features-input"
            />
          </div>
        </>
      ) : (
        <>
          <div>
            <Label>{t("admin_billing:field_name_el")}</Label>
            <Input
              value={form.name_el}
              onChange={(e) => setForm({ ...form, name_el: e.target.value })}
              className="mt-1"
              placeholder={t("admin_billing:field_name_el_placeholder")}
              data-testid="plan-name-el-input"
            />
          </div>
          <div>
            <Label>{t("admin_billing:field_description_el")}</Label>
            <Textarea
              value={form.description_el}
              onChange={(e) => setForm({ ...form, description_el: e.target.value })}
              className="mt-1"
              rows={2}
              placeholder={t("admin_billing:field_description_el_placeholder")}
              data-testid="plan-description-el-input"
            />
          </div>
          <div>
            <Label>{t("admin_billing:field_features_el")}</Label>
            <Textarea
              value={form.features_el}
              onChange={(e) => setForm({ ...form, features_el: e.target.value })}
              className="mt-1 min-h-[100px]"
              placeholder={t("admin_billing:field_features_el_placeholder")}
              data-testid="plan-features-el-input"
            />
          </div>
        </>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>{t("admin_billing:field_monthly_price")}</Label>
          <Input
            type="number"
            value={form.price_monthly}
            onChange={(e) => setForm({ ...form, price_monthly: parseFloat(e.target.value) || 0 })}
            className="mt-1"
          />
        </div>
        <div>
          <Label>{t("admin_billing:field_yearly_price")}</Label>
          <Input
            type="number"
            value={form.price_yearly}
            onChange={(e) => setForm({ ...form, price_yearly: parseFloat(e.target.value) || 0 })}
            className="mt-1"
          />
        </div>
      </div>
      <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
        <div>
          <p className="text-sm font-medium">{t("admin_billing:active_label")}</p>
          <p className="text-xs text-muted-foreground">{t("admin_billing:active_help")}</p>
        </div>
        <Switch
          checked={form.is_active}
          onCheckedChange={(v) => setForm({ ...form, is_active: v })}
          data-testid="plan-form-active"
        />
      </div>
      <Button type="submit" disabled={loading} className="w-full" data-testid="plan-submit-btn">
        {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}{" "}
        {isEdit ? t("admin_billing:update_plan") : t("admin_billing:create_plan")}
      </Button>
    </form>
  );
}

function AssignForm({ venues, plans, onSuccess }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const prefill = typeof window !== "undefined" ? window.__sub_prefill : null;
  if (typeof window !== "undefined") window.__sub_prefill = null;

  const today = new Date().toISOString().slice(0, 10);
  const oneYearLater = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

  const [form, setForm] = useState({
    venue_id: prefill?.venue_id || "",
    plan_id: prefill?.plan_id || "",
    start_date: prefill?.start_date || today,
    end_date: prefill?.end_date || oneYearLater,
    status: prefill?.status || "active",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.venue_id || !form.plan_id) {
      toast.error(t("admin_billing:toast_pick_venue_plan"));
      return;
    }
    setLoading(true);
    try {
      // Backend now consumes `plan_key`; we send both for backwards compat
      // but the canonical field is plan_key (mirrors venue_plans.plan_key).
      const payload = {
        venue_id: form.venue_id,
        plan_key: form.plan_id,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        status: form.status,
      };
      const res = await axios.post(`${API}/admin/billing/subscriptions/assign`, payload);
      toast.success(t("admin_billing:toast_subscription_saved"));
      onSuccess(res.data);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || t("admin_billing:toast_failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label>{t("admin_billing:field_venue")} *</Label>
        <Select value={form.venue_id} onValueChange={(v) => setForm({ ...form, venue_id: v })}>
          <SelectTrigger className="mt-1" data-testid="assign-venue-select">
            <SelectValue placeholder={t("admin_billing:select_venue_placeholder")} />
          </SelectTrigger>
          <SelectContent>
            {venues.map((v) => (
              <SelectItem key={v.venue_id} value={v.venue_id}>
                {(lang === "el" && v.name_el) || v.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>{t("admin_billing:field_plan")} *</Label>
        <Select value={form.plan_id} onValueChange={(v) => setForm({ ...form, plan_id: v })}>
          <SelectTrigger className="mt-1" data-testid="assign-plan-select">
            <SelectValue placeholder={t("admin_billing:select_plan_placeholder")} />
          </SelectTrigger>
          <SelectContent>
            {plans.map((p) => (
              <SelectItem key={p.plan_id} value={p.plan_id}>
                {((lang === "el" && p.name_el) || p.name)} — ${p.price_monthly}{t("admin_billing:per_month")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>{t("admin_billing:field_start_date")}</Label>
          <Input
            type="date"
            value={form.start_date}
            onChange={(e) => setForm({ ...form, start_date: e.target.value })}
            required
            className="mt-1"
            data-testid="assign-start-date"
          />
        </div>
        <div>
          <Label>{t("admin_billing:field_end_date")}</Label>
          <Input
            type="date"
            value={form.end_date}
            onChange={(e) => setForm({ ...form, end_date: e.target.value })}
            required
            className="mt-1"
            data-testid="assign-end-date"
          />
        </div>
      </div>
      <div>
        <Label>{t("admin_billing:field_status")}</Label>
        <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
          <SelectTrigger className="mt-1" data-testid="assign-status-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">{t("admin_billing:status_active")}</SelectItem>
            <SelectItem value="paused">{t("admin_billing:status_paused")}</SelectItem>
            <SelectItem value="expired">{t("admin_billing:status_expired")}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Button type="submit" disabled={loading} className="w-full" data-testid="assign-submit-btn">
        {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} {t("admin_billing:save_subscription")}
      </Button>
    </form>
  );
}
